import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  IconButton,
  MenuItem
} from '@mui/material';
import { areConnectorsCompatible, compatibilityMatrix } from '../services/compatibilityMatrix';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ConnectorImageZoom from './ConnectorImageZoom';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { findConnectorWithSkuCode } from '../services/utilityService';
import ConnectorCompatibilityIndicator from './ConnectorCompatibilityIndicator';
import CloseIcon from '@mui/icons-material/Close';
import { getAvailableWireTypes, getDefaultWireType, wireTypes } from '../services/wireCompatibilityService';
import { cableImageCaptureService } from '../services/cableImageCaptureService';
import clientImageCaptureService from '../services/clientImageCaptureService';
import { buildConnectorFamilies, getFamilyDescription } from '../services/connectorFamiliesService';
import { generateSKU } from '../services/skuService';
import { API_ENDPOINTS } from '../config';
import html2canvas from 'html2canvas';
// Removed html-to-image import - now using EC2 system for image generation


// All points are audio for Zip (Audio + Audio)
const connectorPoints = [
  { id: 'G', label: 'G', type: 'audio' },
  { id: 'H', label: 'H', type: 'audio' },
  { id: 'I', label: 'I', type: 'audio' },
  { id: 'J', label: 'J', type: 'audio' }
];

const lengthLabels = [
  { id: 'A', label: 'A (Overall)' },
  { id: 'B', label: 'B (split to split)' },
  { id: 'C', label: 'C (split to G)' },
  { id: 'D', label: 'D (split to H)' },
  { id: 'E', label: 'E (split to I)' },
  { id: 'F', label: 'F (split to J)' }
];

const defaultLengths = {
  A: 48,  // Overall length
  B: 36,  // split to split - main trunk
  C: 6,   // split to G
  D: 6,   // split to H
  E: 6,   // split to I
  F: 6    // split to J
};

const PX_PER_INCH = 6; // Unified scaling for all segments

// Define connector families for audio (used in the modal)
// const audioConnectorFamilies = [
//   // ... connector families definition
// ];

// Helper component for connector selection modal
function ConnectorModal({ selectedPoint, connectors, onConnectorSelect, type, findConnectorVariant }) {
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Use the type prop if provided, otherwise default to 'audio'
  const cableType = type || 'audio';
  const families = buildConnectorFamilies(cableType);

  // Define pairs for Zip cables
  const pairs = { 'G': 'H', 'H': 'G', 'I': 'J', 'J': 'I' };
  const pairedPoint = pairs[selectedPoint];
  const pairedConnector = connectors?.[pairedPoint];

  // Function to check compatibility for a potential connector
  const checkConnectorCompatibility = (connector) => {
    if (!pairedConnector?.id) {
      return { status: 'compatible', message: 'Compatible' };
    }
    
    try {
      // For the paired connector, we need to consider the configuration variant
      let pairedId = pairedConnector.id;
      
      // If the paired connector has a configuration that's different from 'standard',
      // we need to find the variant connector ID
      if (pairedConnector.configuration && pairedConnector.configuration !== 'standard') {
        const variantConnector = findConnectorVariant(pairedConnector, pairedConnector.configuration);
        if (variantConnector && variantConnector.id) {
          pairedId = variantConnector.id;
        }
      }
      
      const compatibilityResult = areConnectorsCompatible(pairedId, connector.id);
      
      if (!compatibilityResult) {
        return { status: 'incompatible', message: 'Not compatible' };
      }
      
      if (compatibilityResult.compatible === false) {
        return { 
          status: 'incompatible', 
          message: compatibilityResult.reason || 'Incompatible',
          notes: compatibilityResult.details || null
        };
      }
      
      if (compatibilityResult.warning) {
        return { 
          status: 'warning', 
          message: compatibilityResult.warning,
          notes: compatibilityResult.notes 
        };
      }
      
      return { status: 'compatible', message: 'Compatible' };
    } catch (error) {
      console.warn('Error checking compatibility:', error);
      return { status: 'incompatible', message: 'Error checking compatibility' };
    }
  };

  useEffect(() => {
    // Just set loading to false immediately since we don't need to load filtered connectors
    setLoading(false);
  }, [selectedPoint, connectors]);

  // Handle family selection
  const handleFamilySelect = (family) => {
    setSelectedFamily(family.id);
  };

  // Handle connector selection
  const handleConnectorSelect = (connector) => {
    // Map mountType from compatibility matrix to configuration value
    let autoConfiguration = 'standard';
    if (connector.mountType === 'rightangle' || connector.mountType === 'right-angle') {
      autoConfiguration = 'right-angle';
    } else if (connector.mountType === 'panel') {
      autoConfiguration = 'panel-mount';
    } else if (connector.mountType === 'cable') {
      autoConfiguration = 'standard';
    }
    
    // Convert to legacy format that the parent expects
    const legacyConnector = {
      id: connector.id,
      label: connector.description || connector.Label || connector.label || connector.id,
      gender: connector.gender,
      image: connector.image,
      family: connector.family,
      type: connector.purpose?.split(',')[0] || connector.type || 'audio', // Use first purpose
      brand: 'Professional',
      configuration: autoConfiguration, // Use mapped configuration instead of always 'standard'
      skuCode: connector.skuCode,
      // Add all the fields that the parent expects
      description: connector.description || connector.Label || connector.label || connector.id,
      mountType: connector.mountType || 'cable',
      poles: connector.poles || 2,
      // Ensure all required fields are present
      Label: connector.description || connector.Label || connector.label || connector.id
    };
    
    onConnectorSelect(selectedPoint, legacyConnector);
  };

  // Go back to family selection
  const handleBackToFamilies = () => {
    setSelectedFamily(null);
  };

  // Get the selected family object
  const selectedFamilyObj = families.find(f => f.id === selectedFamily);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography>Loading compatible connectors...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxHeight: '50vh', overflowY: 'auto', mt: 1 }}>
      {/* Compatibility explanation when paired connector exists */}
      {pairedConnector?.id && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Compatibility Check Active:</strong> Showing compatibility with your selected {pairedPoint} connector. 
            Green = Compatible, Yellow = Warning, Red = Incompatible
          </Typography>
        </Alert>
      )}
      
      {!selectedFamily ? (
        // Step 1: Show connector families
        <>
          <Typography variant="body2" color="text.secondary" paragraph>
            Choose the type of connector you need:
          </Typography>
          <Grid container spacing={2}>
            {families.length > 0 ? (
              families.map((family) => (
                <Grid item xs={12} sm={6} key={family.id}>
                  <Paper
                    sx={{
                      cursor: 'pointer',
                      p: 2,
                      height: '100%',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onClick={() => handleFamilySelect(family)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ width: { xs: 36, sm: 48 }, height: { xs: 36, sm: 48 }, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: 4, p: 0.5, overflow: 'hidden' }}>
                        <img
                          src={family.image}
                          alt={family.name}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {family.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {family.description}
                        </Typography>
                        <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                          {family.variants.length} options available
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    {pairedConnector?.id ? 
                      'No compatible connectors found with your selected connector. Please try a different connector type.' :
                      `No connectors found for type: ${cableType}. Please check the compatibility matrix configuration.`
                    }
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </>
      ) : (
        // Step 2: Show specific connectors in selected family
        <>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              variant="body2" 
              color="primary" 
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={handleBackToFamilies}
            >
              ← Back to connector types
            </Typography>
            <Typography variant="body1">
              <strong>{selectedFamilyObj?.name}</strong> - {selectedFamilyObj?.description}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            {selectedFamilyObj?.variants.map((connector) => {
              // Use the new compatibility check function
              const compatibilityResult = checkConnectorCompatibility(connector);
              const isIncompatible = compatibilityResult?.status === 'incompatible';
              const hasPairedConnector = pairedConnector?.id;
              
              return (
                <Grid item xs={12} key={connector.id}>
                  <Paper
                    sx={{ 
                      cursor: isIncompatible ? 'not-allowed' : 'pointer', 
                      opacity: isIncompatible ? 0.6 : 1,
                      border: hasPairedConnector ? (
                        compatibilityResult?.status === 'incompatible' ? '2px solid #f44336' : 
                        compatibilityResult?.status === 'warning' ? '2px solid #ffb300' : 
                        compatibilityResult?.status === 'compatible' ? '2px solid #4caf50' : '1px solid #e0e0e0'
                      ) : '1px solid #e0e0e0',
                      '&:hover': { 
                        boxShadow: isIncompatible ? 1 : 6,
                        transform: isIncompatible ? 'none' : 'translateY(-2px)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onClick={() => !isIncompatible && handleConnectorSelect(connector)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                      <Box sx={{ width: { xs: 36, sm: 48 }, height: { xs: 36, sm: 48 }, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', '& img': { width: '100%', height: '100%', objectFit: 'contain' } }}>
                        <ConnectorImageZoom src={connector.image} alt={connector.name} style={{ width: '100%', height: '100%' }} />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1">
                          {connector.name}
                          {connector.id === 'NC5FXX' || connector.id === 'NC5MXX' ? (
                            <Tooltip title="Only 3 pins wired" arrow>
                              <span style={{ color: '#ff9800', marginLeft: '4px', fontWeight: 'bold' }}>*</span>
                            </Tooltip>
                          ) : null}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {connector.gender} • {connector.mountType === 'cable' ? 'Cable Mount' : connector.mountType === 'panel' ? 'Panel Mount' : connector.mountType}
                        </Typography>
                        {isIncompatible && (
                          <Typography variant="caption" color="error.main" sx={{ fontWeight: 'bold' }}>
                            Incompatible with selected connector
                          </Typography>
                        )}
                      </Box>
                      {/* Compatibility Indicator */}
                      {hasPairedConnector && (
                        <Box sx={{ ml: 2 }}>
                          <ConnectorCompatibilityIndicator result={compatibilityResult} />
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </Box>
  );
}

// Check if connector is Speakon (RA not available)
function isSpeakonConnector(connector) {
  if (!connector) return false;
  return connector.family === 'Speakon' || 
         (connector.label?.toLowerCase().includes('speakon') ?? false);
}

// Helper function to check if connector is XLR 5-pin
function isXLR5Pin(connector) {
  return connector?.id === 'NC5FXX' || connector?.id === 'NC5MXX';
}



// Get the appropriate image for connectors based on configuration
function getConnectorImage(connector, configuration) {
  // Use the image property directly from the compatibility matrix entry
  // This ensures we're using the centralized source of truth for image paths
  if (!connector?.image) {
    return '/images/coming_soon.png'; // Fallback for missing images
  }
  
  // For most connectors, return the image directly from the compatibility matrix
  // The compatibility matrix already contains the correct image paths
  return connector.image;
}

// Helper function to map configuration to mount type
const mapConfigurationToMountType = (configuration) => {
  switch (configuration) {
    case 'standard':
      return 'cable';
    case 'panel-mount':
      return 'panel';
    case 'right-angle':
      return 'rightangle'; // Note: some connectors use 'right-angle', others use 'rightangle'
    default:
      return configuration;
  }
};

// Helper function to find the correct connector variant based on configuration
const findConnectorVariant = (currentConnector, configuration) => {
  if (!currentConnector || !currentConnector.id) return currentConnector;
  
  // Get the current connector's data from the compatibility matrix
  const currentConnectorData = compatibilityMatrix[currentConnector.id];
  if (!currentConnectorData) {
    return {
      ...currentConnector,
      configuration: configuration
    };
  }
  
  const { family, type, purpose, gender, poles } = currentConnectorData;
  const targetMountType = mapConfigurationToMountType(configuration);
  
  // Search through the compatibility matrix to find the variant with the same family, type, purpose, gender, and poles
  // but with the desired mount type (configuration)
  for (const [connectorId, connectorData] of Object.entries(compatibilityMatrix)) {
    // Handle comma-separated purposes like 'audio, hybrid' or 'audio,hybrid'
    const connectorPurposes = connectorData.purpose ? 
      connectorData.purpose.split(',').map(p => p.trim().toLowerCase()) : 
      [];
    
    const targetPurposes = purpose ? 
      purpose.split(',').map(p => p.trim().toLowerCase()) : 
      [];
    
    // Check if any of the connector's purposes match any of the target purposes
    const purposeMatches = connectorPurposes.some(connectorPurpose => 
      targetPurposes.includes(connectorPurpose)
    );
    
    if (connectorData.family === family && 
        connectorData.type === type &&
        purposeMatches &&
        connectorData.gender === gender &&
        connectorData.poles === poles &&
        (connectorData.mountType === targetMountType || 
         (targetMountType === 'rightangle' && connectorData.mountType === 'right-angle'))) {
      
      // Return the complete connector object with the new ID and all updated properties
      const updatedConnector = {
        ...currentConnector,
        id: connectorId,
        skuCode: connectorData.skuCode || connectorId, // Use skuCode from matrix, fallback to id
        configuration: configuration,
        image: connectorData.image || currentConnector.image, // Use new image or fallback to current
        description: connectorData.description || connectorData.name || currentConnector.description,
        gender: connectorData.gender || currentConnector.gender,
        mountType: connectorData.mountType,
        family: connectorData.family || currentConnector.family,
        type: connectorData.type || currentConnector.type,
        purpose: connectorData.purpose || currentConnector.purpose,
        label: connectorData.name || connectorData.description || currentConnector.label
      };
      
      return updatedConnector;
    }
  }
  
  // If no variant found, return the original connector with updated configuration
  return {
    ...currentConnector,
    configuration: configuration
  };
};

// Helper function to check if a configuration variant exists for a connector
// Now imported from utilityService.js - uses compatibility matrix as single source of truth
// const getAvailableConfigurations = (connector) => { ... };

function ZipAudioAudioConnectorSelection({ selectedConnectorId, ...rest }) {
  // Extract connectors from the value prop
  const connectors = rest.value?.connectors || {};
  
  // State for showing tooltip notifications
  const [tooltipMessage, setTooltipMessage] = useState('');
  const [tooltipOpen, setTooltip] = useState(false);
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [lengths, setLengths] = useState(() => ({
    ...defaultLengths,
    ...(rest.value?.lengths || {})
  }));
  const [specialInstructions, setSpecialInstructions] = useState(rest.value?.specialInstructions || '');
  const [unit, setUnit] = useState('in');
  const [overallInput, setOverallInput] = useState(unit === 'ft' ? parseFloat(rest.value?.lengths?.A).toFixed(2).replace(/\.00$/, '') : rest.value?.lengths?.A || '');
  const [touchedLengths, setTouchedLengths] = useState({});
  const [lastManualEdit, setLastManualEdit] = useState(null); // Track which field was last manually edited
  const [isEditingOverall, setIsEditingOverall] = useState(false); // Track if user is editing overall length
  const [editedFields, setEditedFields] = useState(new Set()); // Track which fields have been manually edited
  const [tempInputs, setTempInputs] = useState({}); // Track temporary input values during typing
  
  // SVG pan and zoom state
  const [svgTransform, setSvgTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  
  // Internal SVG ref for wheel event handling
  const localSvgRef = useRef(null);
  const actualSvgRef = rest.svgRef || localSvgRef;

  // Compatibility state
  const [compatibilityWarnings, setCompatibilityWarnings] = useState({});
  
  // Wire type state for audio connectors - Zip cables always use PSZ
  const [audioWireType, setAudioWireType] = useState('PSZ');
  const [availableAudioWireTypes, setAvailableAudioWireTypes] = useState([]);

  // Reset to default state only when value prop is completely undefined (when switching cable types)
  useEffect(() => {
    if (rest.value === undefined) {
      console.log('🔄 ZipAudioAudioConnectorSelection: Resetting to default state (cable type switch)');
      setLengths(defaultLengths);
      setSpecialInstructions('');
      setAudioWireType('PSZ');
      setEditedFields(new Set());
      setLastManualEdit(null);
      setOverallInput('48'); // Default A length
      setCompatibilityWarnings({});
      setAvailableAudioWireTypes([]);
    }
  }, [rest.value === undefined]);

  // Update state when value prop changes (for data persistence)
  useEffect(() => {
    if (rest.value) {
      if (rest.value.lengths) {
        setLengths(prev => ({ ...prev, ...rest.value.lengths }));
      }
      if (rest.value.specialInstructions !== undefined) {
        setSpecialInstructions(rest.value.specialInstructions);
      }
      if (rest.value.audioWireType) {
        setAudioWireType(rest.value.audioWireType);
      }
    }
  }, [rest.value]);

  // Helper function to convert relative paths to absolute URLs for client-side rendering
  const getImageUrlForDisplay = useCallback((imagePath) => {
    if (!imagePath) return imagePath;
    if (imagePath.startsWith('data:') || imagePath.startsWith('http')) {
      return imagePath;
    }
    // Convert relative URL to absolute URL for client-side rendering
    return imagePath.startsWith('/') 
      ? `${window.location.origin}${imagePath}`
      : `${window.location.origin}/${imagePath}`;
  }, []);

  // Memoize connector images to prevent repeated getConnectorImage calls
  const connectorImages = useMemo(() => {
    const images = {};
    if (!connectors) return images; // Early return if connectors is undefined
    
    ['G', 'H', 'I', 'J'].forEach(point => {
      const connector = connectors[point];
      if (connector?.id) {
        const imageUrl = getConnectorImage(connector, connector.configuration || 'standard');
        // Store relative paths directly for consistent Puppeteer capture
        images[point] = imageUrl;
      }
    });
    return images;
  }, [connectors.G?.id, connectors.G?.configuration, connectors.H?.id, connectors.H?.configuration, connectors.I?.id, connectors.I?.configuration, connectors.J?.id, connectors.J?.configuration]);

  // Update available audio wire types when audio connectors change
  useEffect(() => {
    // Get all audio connectors (G, H, I, J)
    const audioConnectors = [connectors.G, connectors.H, connectors.I, connectors.J].filter(Boolean);
    
    if (audioConnectors.length >= 2) {
      // Get wire types compatible with all audio connectors
      const allWireTypes = audioConnectors.map(conn => getAvailableWireTypes(conn.id));
      
      // Find intersection of all compatible wire types
      if (allWireTypes.length > 0) {
        const firstWireIds = allWireTypes[0].map(wire => wire.id);
        const commonWireIds = firstWireIds.filter(id => 
          allWireTypes.every(wireTypes => wireTypes.some(wire => wire.id === id))
        );
        
        // Filter out 'Zip' since zip cables always use PSZ
        const filteredWireIds = commonWireIds.filter(id => id !== 'Zip');
        
        const commonWires = filteredWireIds.map(id => wireTypes[id]).filter(Boolean);
        setAvailableAudioWireTypes(commonWires);
        
        // Zip cables always use PSZ, so no need to change audioWireType
      }
    }
  }, [connectors.G?.id, connectors.H?.id, connectors.I?.id, connectors.J?.id]);

  // Zip cables always use PSZ, so no need to sync with value prop
  useEffect(() => {
    // Always maintain PSZ for zip cables
    if (audioWireType !== 'PSZ') {
      setAudioWireType('PSZ');
    }
  }, [audioWireType]);

  // Check compatibility between selected connectors (all audio)
  useEffect(() => {
    const checkCompatibility = () => {
      const warnings = {};
      
      if (!connectors) {
        setCompatibilityWarnings(warnings);
        return;
      }
      
      // For Zip Audio-Audio cables, connectors are paired:
      // G ↔ H (audio1 pair)
      // I ↔ J (audio2 pair)
      const pairs = [
        ['G', 'H'],
        ['I', 'J']
      ];
      
      for (const [connA_id, connB_id] of pairs) {
        const connA = connectors?.[connA_id];
        const connB = connectors?.[connB_id];
        
        if (connA?.id && connB?.id) {
          try {
            const compatResult = areConnectorsCompatible(connA.id, connB.id);
            if (!compatResult?.compatible) {
              const key = `${connA_id}-${connB_id}`;
              warnings[key] = {
                type: 'warning',
                message: `Connectors ${connA.label || connA.id} and ${connB.label || connB.id} are not compatible.`,
                details: 'Please select compatible connector pairs.'
              };
            }
          } catch (error) {
            console.warn('Error checking compatibility:', error);
          }
        }
      }
      
      setCompatibilityWarnings(warnings);
    };
    
    checkCompatibility();
  }, [connectors.G?.id, connectors.H?.id, connectors.I?.id, connectors.J?.id]);

  // Filter connectors based on compatibility with already selected connectors
  const getFilteredConnectors = useCallback(async (forPoint) => {
    const cableType = rest.type || 'audio';
    const families = buildConnectorFamilies(cableType);
    const pairs = { 'G': 'H', 'H': 'G', 'I': 'J', 'J': 'I' };
    const pairedPoint = pairs[forPoint];
    const pairedConnector = connectors?.[pairedPoint];
    
    if (!pairedConnector?.id) return [];
    
    // Get all connectors from all families
    const allConnectors = families.flatMap(family => family.variants);
    
    // Filter connectors to only include compatible ones
    const filteredConnectors = allConnectors.filter(connector => {
      try {
        // For the paired connector, we need to consider the configuration variant
        let pairedId = pairedConnector.id;
        
        // If the paired connector has a configuration that's different from 'standard',
        // we need to find the variant connector ID
        if (pairedConnector.configuration && pairedConnector.configuration !== 'standard') {
          const variantConnector = findConnectorVariant(pairedConnector, pairedConnector.configuration);
          if (variantConnector && variantConnector.id) {
            pairedId = variantConnector.id;
          }
        }
        
        const compatibilityResult = areConnectorsCompatible(pairedId, connector.id);
        return compatibilityResult?.compatible;
      } catch (error) {
        console.warn('Error checking compatibility:', error);
        return false;
      }
    });
    
    return filteredConnectors;
  }, [connectors.G?.id, connectors.G?.configuration, connectors.H?.id, connectors.H?.configuration, connectors.I?.id, connectors.I?.configuration, connectors.J?.id, connectors.J?.configuration, rest.type]);

  const handleConnectorSelect = (point, connector) => {
    const skuCode = findConnectorWithSkuCode(connector);
    
    // Map mountType from compatibility matrix to configuration value
    let autoConfiguration = 'standard';
    if (connector.mountType === 'rightangle' || connector.mountType === 'right-angle') {
      autoConfiguration = 'right-angle';
    } else if (connector.mountType === 'panel') {
      autoConfiguration = 'panel-mount';
    } else if (connector.mountType === 'cable') {
      autoConfiguration = 'standard';
    }
    
    const newConnectors = {
      ...(connectors || {}),
      [point]: {
        ...connector,
        id: connector.id,
        skuCode,
        configuration: autoConfiguration,
        // Ensure all required fields are present for review page
        label: connector.label,
        type: connector.type,
        gender: connector.gender,
        family: connector.family,
        image: connector.image
      }
    };
    
    // Update the value prop structure that the parent expects
    rest.onChange({
      connectors: newConnectors,
      lengths,
      specialInstructions
    });
    
    setModalOpen(false);
    setSelectedPoint(null);
  };

  // Helper: clear connector for a specific point
  const handleClearConnector = useCallback((pointId) => {
    const updatedConnectors = { ...(connectors || {}) };
    delete updatedConnectors[pointId];
    rest.onChange({
      connectors: updatedConnectors,
      lengths,
      specialInstructions
    });
  }, [connectors, lengths, specialInstructions, rest]);

  const handleConfigurationChange = (point, configuration, event) => {
    const currentConnector = connectors?.[point];
    if (!currentConnector) return;
    
    // Find the correct connector variant for this configuration
    const newConnector = findConnectorVariant(currentConnector, configuration);
    
    // Check compatibility with the paired connector before making the change
    const pairs = { 'G': 'H', 'H': 'G', 'I': 'J', 'J': 'I' };
    const pairedPoint = pairs[point];
    const pairedConnector = connectors?.[pairedPoint];
    
    if (pairedConnector?.id) {
      // For the paired connector, we need to consider the configuration variant
      let pairedId = pairedConnector.id;
      
      // If the paired connector has a configuration that's different from 'standard',
      // we need to find the variant connector ID
      if (pairedConnector.configuration && pairedConnector.configuration !== 'standard') {
        const variantConnector = findConnectorVariant(pairedConnector, pairedConnector.configuration);
        if (variantConnector && variantConnector.id) {
          pairedId = variantConnector.id;
        }
      }
      
      const compatibilityResult = areConnectorsCompatible(pairedId, newConnector.id);
      
      if (!compatibilityResult) {
        // If areConnectorsCompatible returns null, it means no compatibility rule exists = incompatible
        const message = 'Not compatible with paired connector';
        setTooltipMessage(`Configuration change prevented: ${message}`);
        setTooltipAnchor(event?.currentTarget);
        setTooltip(true);
        
        // Auto-hide tooltip after 3 seconds
        setTimeout(() => {
          setTooltip(false);
        }, 3000);
        
        return; // Don't update the state
      }
      
      if (compatibilityResult && !compatibilityResult.compatible) {
        const message = compatibilityResult.reason || compatibilityResult.message || 'Incompatible with paired connector';
        setTooltipMessage(`Configuration change prevented: ${message}`);
        setTooltipAnchor(event?.currentTarget);
        setTooltip(true);
        
        // Auto-hide tooltip after 3 seconds
        setTimeout(() => {
          setTooltip(false);
        }, 3000);
        
        return; // Don't update the state
      }
    }
    
    const newConnectors = {
      ...(connectors || {}),
      [point]: newConnector
    };
    
    rest.onChange({
      connectors: newConnectors,
      lengths,
      specialInstructions
    });
  };

  // Helper function to get available configurations for a specific connector
  const getConfigurationsForConnector = useCallback((connector) => {
    if (!connector?.id) return { standard: true, 'right-angle': true, 'panel-mount': true };
    
    // Get the connector data from the compatibility matrix
    const connectorData = compatibilityMatrix[connector.id];
    if (!connectorData) {
      return { standard: true, 'right-angle': true, 'panel-mount': true };
    }
    
    const { family, type, purpose, gender, poles } = connectorData;
    
    // Search through the compatibility matrix to find all variants with the same family, type, purpose, gender, AND poles
    const availableMountTypes = new Set();
    
    Object.values(compatibilityMatrix).forEach(connector => {
      // Handle comma-separated purposes like 'audio, hybrid' or 'audio,hybrid'
      const connectorPurposes = connector.purpose ? 
        connector.purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      const targetPurposes = purpose ? 
        purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      // Check if any of the connector's purposes match any of the target purposes
      const purposeMatches = connectorPurposes.some(connectorPurpose => 
        targetPurposes.includes(connectorPurpose)
      );
      
      if (connector.family === family && 
          connector.type === type && 
          purposeMatches &&
          connector.gender === gender &&
          connector.poles === poles) {
        availableMountTypes.add(connector.mountType);
      }
    });

    // Map mountType values to configuration names
    const hasStandard = availableMountTypes.has('cable');
    const hasRightAngle = availableMountTypes.has('rightangle') || availableMountTypes.has('right-angle');
    const hasPanelMount = availableMountTypes.has('panel');

    return {
      standard: hasStandard,
      'right-angle': hasRightAngle,
      'panel-mount': hasPanelMount
    };
  }, []);

  // Helper function to check if a configuration would be incompatible with the paired connector
  const checkConfigurationCompatibility = useCallback((point, configuration) => {
    const currentConnector = connectors?.[point];
    if (!currentConnector?.id) return null;
    
    // First check if this configuration actually exists for this connector
    const currentConnectorData = compatibilityMatrix[currentConnector.id];
    if (!currentConnectorData) return null;
    
    const { family, type, purpose, poles } = currentConnectorData;
    const targetMountType = mapConfigurationToMountType(configuration);
    
    // Check if a variant with this configuration actually exists
    let variantExists = false;
    for (const [, connectorData] of Object.entries(compatibilityMatrix)) {
      // Handle comma-separated purposes like 'audio, hybrid' or 'audio,hybrid'
      const connectorPurposes = connectorData.purpose ? 
        connectorData.purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      const targetPurposes = purpose ? 
        purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      // Check if any of the connector's purposes match any of the target purposes
      const purposeMatches = connectorPurposes.some(connectorPurpose => 
        targetPurposes.includes(connectorPurpose)
      );
      
      if (connectorData.family === family && 
          connectorData.type === type &&
          purposeMatches &&
          connectorData.poles === poles &&
          (connectorData.mountType === targetMountType || 
           (targetMountType === 'rightangle' && connectorData.mountType === 'right-angle'))) {
        variantExists = true;
        break;
      }
    }
    
    // If the configuration doesn't exist, return null (will be disabled, not red)
    if (!variantExists) return null;
    
    // Find the connector variant for this configuration
    const newConnector = findConnectorVariant(currentConnector, configuration);
    if (!newConnector) return null;
    
    // Check compatibility with the paired connector
    const pairs = { 'G': 'H', 'H': 'G', 'I': 'J', 'J': 'I' };
    const pairedPoint = pairs[point];
    const pairedConnector = connectors?.[pairedPoint];
    if (!pairedConnector?.id) return null;
    
    // For the paired connector, we need to consider the configuration variant
    let pairedId = pairedConnector.id;
    
    // If the paired connector has a configuration that's different from 'standard',
    // we need to find the variant connector ID
    if (pairedConnector.configuration && pairedConnector.configuration !== 'standard') {
      const variantConnector = findConnectorVariant(pairedConnector, pairedConnector.configuration);
      if (variantConnector && variantConnector.id) {
        pairedId = variantConnector.id;
      }
    }
    
    const compatResult = areConnectorsCompatible(pairedId, newConnector.id);
    
    if (!compatResult) {
      // If areConnectorsCompatible returns null, it means no compatibility rule exists = incompatible
      return {
        incompatible: true,
        reason: 'Not compatible with paired connector'
      };
    }
    
    if (compatResult && !compatResult.compatible) {
      return {
        incompatible: true,
        reason: compatResult.reason || compatResult.message || 'Incompatible with paired connector'
      };
    }
    
    return { incompatible: false };
  }, [connectors.G?.id, connectors.G?.configuration, connectors.H?.id, connectors.H?.configuration, connectors.I?.id, connectors.I?.configuration, connectors.J?.id, connectors.J?.configuration]);



  // Length handling functions
  const handleLengthFocus = (id) => {
    setEditedFields(prev => new Set([...prev, id]));
    // Initialize temp input with current display value
    const currentVal = unit === 'ft' ? (parseFloat(lengths[id] || 0) / 12).toFixed(2).replace(/\.00$/, '') : lengths[id] || '';
    setTempInputs(prev => ({ ...prev, [id]: String(currentVal) }));
  };

  const handleLengthBlur = (id) => {
    commitLengthInput(id);
  };

  const commitLengthInput = (id) => {
    const val = tempInputs[id] || '';
    let v = parseFloat(val);
    if (isNaN(v) || v < 0) v = '';
    else {
      if (unit === 'ft') {
        // Input is in feet, convert to inches for internal storage
        // Cap branch lengths (C, D, E, F) at 10 feet, overall length (A) at 500 feet
        if (id === 'A') {
          if (v > 500) v = 500;
        } else if (['C', 'D', 'E', 'F'].includes(id)) {
          if (v > 10) v = 10;
        } else {
          if (v > 500) v = 500;
        }
        v = v * 12; // Convert feet to inches
        // Round to nearest whole inch
        v = Math.round(v);
      } else {
        // Input is in inches, store directly
        // Round to nearest whole inch
        v = Math.round(v);
        // Cap branch lengths (C, D, E, F) at 120 inches (10 feet), overall length (A) at 6000 inches (500 feet)
        if (id === 'A') {
          if (v > 6000) v = 6000;
        } else if (['C', 'D', 'E', 'F'].includes(id)) {
          if (v > 120) v = 120;
        } else {
          if (v > 6000) v = 6000;
        }
      }
    }
    const newLengths = { ...lengths, [id]: v };
    
    // If C, D, E, or F was changed, check if A needs to be increased
    let updateA = false;
    if (['C', 'D', 'E', 'F'].includes(id)) {
      const longerCE = Math.max(parseFloat(newLengths['C'] || 0), parseFloat(newLengths['E'] || 0));
      const longerDF = Math.max(parseFloat(newLengths['D'] || 0), parseFloat(newLengths['F'] || 0));
      const minB = 6; // Minimum B length
      const minRequiredOverall = longerCE + longerDF + minB;
      
      if (newLengths['A'] < minRequiredOverall) {
        newLengths['A'] = minRequiredOverall;
        updateA = true;
      }
    }
    
    setLengths(newLengths);
    setEditedFields(prev => new Set([...prev, id]));
    setLastManualEdit(id); // Track this as a manual edit
    setTempInputs(prev => {
      const newTemp = { ...prev };
      delete newTemp[id]; // Clear temp input after committing
      return newTemp;
    });
    
    // Update the overallInput field if A was automatically increased
    if (updateA) {
      setEditedFields(prev => new Set([...prev, 'A']));
      const displayValue = unit === 'ft' ? (parseFloat(newLengths['A']) / 12).toFixed(2).replace(/\.00$/, '') : newLengths['A'].toString();
      setOverallInput(displayValue);
    }
    
    // CRITICAL: Always pass lengths in inches to parent component
    rest.onChange({ connectors: connectors || {}, lengths: newLengths, specialInstructions, audioWireType });
  };

  const isEdited = (id) => {
    return editedFields.has(id);
  };

  const handleOverallInputChange = (val) => {
    // Allow free typing without validation - only store the input value
    setOverallInput(val);
    setLastManualEdit('A'); // Track this as a manual edit
  };

  const commitOverallInput = () => {
    let v = parseFloat(overallInput);
    if (isNaN(v) || v < 0) v = 0;
    if (unit === 'ft') {
      // Cap at 500 feet before conversion
      if (v > 500) v = 500;
      v = v * 12; // Convert feet to inches
      // Round to nearest whole inch
      v = Math.round(v);
    } else {
      // Cap at 6000 inches (500 feet)
      if (v > 6000) v = 6000;
      // Round to nearest whole inch
      v = Math.round(v);
    }
    
    // Calculate the minimum required overall length based on branch lengths
    const longerCE = Math.max(parseFloat(lengths['C'] || 0), parseFloat(lengths['E'] || 0));
    const longerDF = Math.max(parseFloat(lengths['D'] || 0), parseFloat(lengths['F'] || 0));
    const minB = 6; // Minimum B length
    const minRequiredOverall = longerCE + longerDF + minB;
    
    // Ensure overall length is never less than minimum required
    if (v < minRequiredOverall) {
      v = minRequiredOverall;
      // Update the input field to show the corrected value
      const displayValue = unit === 'ft' ? (parseFloat(v) / 12).toFixed(2).replace(/\.00$/, '') : v.toString();
      setOverallInput(displayValue);
    }
    
    const newLengths = { ...lengths, A: v };
    setLengths(newLengths);
    setEditedFields(prev => new Set([...prev, 'A'])); // Mark A as edited when committed
    setLastManualEdit('A'); // Track this as a manual edit of A
    
    // Update the overallInput display value to reflect the rounded value
    const displayValue = unit === 'ft' ? (parseFloat(v) / 12).toFixed(2).replace(/\.00$/, '') : v.toString();
    setOverallInput(displayValue);
    
    // CRITICAL: Always pass lengths in inches to parent component
    rest.onChange({ connectors: connectors || {}, lengths: newLengths, specialInstructions, audioWireType });
  };


  const calculateLengthB = (lengths) => {
    // B = A - (longer of G/I + longer of H/J), with minimum based on unit
    const longerGI = Math.max(parseFloat(lengths['C'] || 0), parseFloat(lengths['E'] || 0));
    const longerHJ = Math.max(parseFloat(lengths['D'] || 0), parseFloat(lengths['F'] || 0));
    const calculatedB = parseFloat(lengths['A'] || 0) - (longerGI + longerHJ);
    const minB = unit === 'ft' ? 0.5 : 6; // Minimum 0.5' or 6" for B
    return Math.max(minB, calculatedB);
  };

  const calculateRequiredOverallLength = (lengths) => {
    // Calculate the minimum required overall length based on branch lengths + minimum B
    const longerGI = Math.max(parseFloat(lengths['C'] || 0), parseFloat(lengths['E'] || 0));
    const longerHJ = Math.max(parseFloat(lengths['D'] || 0), parseFloat(lengths['F'] || 0));
    const minB = unit === 'ft' ? 0.5 : 6; // Minimum 0.5' or 6" for B
    return longerGI + longerHJ + minB;
  };

  const handleLengthChange = (id, val) => {
    // Just store the temporary input value without rounding
    setTempInputs(prev => ({ ...prev, [id]: val }));
  };

  const handleInstructionsChange = (e) => {
    setSpecialInstructions(e.target.value);
    // CRITICAL: Always pass lengths in inches to parent component
    rest.onChange({ connectors: connectors || {}, lengths, specialInstructions: e.target.value, audioWireType });
  };

  // Zip cables always use PSZ, so no wire type change handler needed

  const handleUnitChange = (event, newUnit) => { 
    if (newUnit) {
      setUnit(newUnit);
      // Update the overallInput display value based on the new unit
      const currentLengthA = lengths['A'] || 0;
      if (newUnit === 'ft') {
        setOverallInput((currentLengthA / 12).toFixed(2).replace(/\.00$/, ''));
      } else {
        setOverallInput(String(currentLengthA));
      }
    }
  };

  // SVG pan and zoom handlers (wheel handled by useEffect with proper passive listener)

  const handleSvgMouseDown = (e) => {
    // Only start panning if we're not over a form element
    const target = e.target;
    const isFormElement = target.closest('input, textarea, select, button, [role="button"]');
    
    if (isFormElement) {
      return;
    }
    
    // Only start panning on left mouse button
    if (e.button === 0) {
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleSvgMouseMove = useCallback((e) => {
    if (!isPanning) return;
    
    // Prevent default browser behavior to ensure smooth panning
    e.preventDefault();
    
    // Only pan if we're not over a form element
    const target = e.target;
    const isFormElement = target.closest('input, textarea, select, button, [role="button"]');
    
    if (isFormElement) {
      setIsPanning(false);
      return;
    }
    
    const deltaX = e.clientX - lastPanPoint.x;
    const deltaY = e.clientY - lastPanPoint.y;
    
    // Enhanced panning for trackpad gestures
    // Trackpad events often have different movement characteristics
    const isTrackpadGesture = e.movementX !== undefined && e.movementY !== undefined;
    
    let adjustedDeltaX = deltaX;
    let adjustedDeltaY = deltaY;
    
    // For trackpad gestures, use movementX/Y if available for more responsive panning
    if (isTrackpadGesture && (e.movementX !== 0 || e.movementY !== 0)) {
      adjustedDeltaX = e.movementX;
      adjustedDeltaY = e.movementY;
    }
    
    setSvgTransform(prev => ({
      ...prev,
      x: prev.x + adjustedDeltaX,
      y: prev.y + adjustedDeltaY
    }));
    setLastPanPoint({ x: e.clientX, y: e.clientY });
  }, [isPanning, lastPanPoint]);

  const handleSvgMouseUp = () => {
    setIsPanning(false);
  };

  // Handle touch events for mobile
  const handleSvgTouchStart = (e) => {
    if (e.touches.length === 1) {
      // Single touch - start panning
      const touch = e.touches[0];
      setIsPanning(true);
      setLastPanPoint({ x: touch.clientX, y: touch.clientY });
    } else if (e.touches.length === 2) {
      // Two finger touch - prevent default to avoid browser zoom
      e.preventDefault();
    }
  };

  const handleSvgTouchMove = useCallback((e) => {
    if (e.touches.length === 1 && isPanning) {
      // Single touch panning - prevent default to ensure smooth panning
      e.preventDefault();
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastPanPoint.x;
      const deltaY = touch.clientY - lastPanPoint.y;
      
      // Enhanced sensitivity for touch panning
      // Touch events may need different sensitivity than mouse events
      const sensitivity = 1.0; // Can be adjusted if needed
      
      setSvgTransform(prev => ({
        ...prev,
        x: prev.x + (deltaX * sensitivity),
        y: prev.y + (deltaY * sensitivity)
      }));
      setLastPanPoint({ x: touch.clientX, y: touch.clientY });
    } else if (e.touches.length === 2) {
      // Two finger pinch zoom towards center of pinch
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      
      // Calculate center of pinch gesture
      const pinchCenterX = (touch1.clientX + touch2.clientX) / 2;
      const pinchCenterY = (touch1.clientY + touch2.clientY) / 2;
      
      // Get SVG bounds
      const svgRect = e.currentTarget.getBoundingClientRect();
      const centerX = pinchCenterX - svgRect.left;
      const centerY = pinchCenterY - svgRect.top;
      
      if (e.scale) {
        // Reduce zoom sensitivity by 50% (multiply by 0.5)
        const sensitivityMultiplier = 0.5;
        const adjustedScale = 1 + (e.scale - 1) * sensitivityMultiplier;
        const newScale = Math.max(0.5, Math.min(6, svgTransform.scale * adjustedScale));
        
        // Calculate new transform to zoom towards the pinch center
        const scaleChange = newScale / svgTransform.scale;
        const newX = svgTransform.x - (centerX - svgTransform.x) * (scaleChange - 1);
        const newY = svgTransform.y - (centerY - svgTransform.y) * (scaleChange - 1);
        
        setSvgTransform({
          x: newX,
          y: newY,
          scale: newScale
        });
      }
    }
  }, [isPanning, lastPanPoint, svgTransform]);

  const handleSvgTouchEnd = () => {
    setIsPanning(false);
  };

  // Reset SVG transform when lengths change
  useEffect(() => {
    setSvgTransform({ x: 0, y: 0, scale: 1 });
  }, [lengths]);

  // Handle wheel events with proper passive listener
  useEffect(() => {
    if (!actualSvgRef) return;
    const svgElement = actualSvgRef.current;
    if (!svgElement) return;

    const handleWheel = (e) => {
      // Only handle wheel events if we're not over a form element
      const target = e.target;
      const isFormElement = target.closest('input, textarea, select, button, [role="button"]');
      
      if (isFormElement) {
        return; // Let the form element handle the event
      }
      
      // Check if this is a trackpad gesture (deltaY is small) vs mouse wheel (deltaY is large)
      const isTrackpadGesture = Math.abs(e.deltaY) < 50;
      
      if (isTrackpadGesture && e.ctrlKey) {
        // Ctrl + trackpad = zoom towards mouse pointer
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        // Reduce zoom sensitivity by 50% (multiply by 0.5)
        const sensitivityMultiplier = 0.5;
        const adjustedDelta = 1 + (delta - 1) * sensitivityMultiplier;
        const newScale = Math.max(0.5, Math.min(6, svgTransform.scale * adjustedDelta));
        
        // Get mouse position relative to SVG
        const svgRect = e.currentTarget.getBoundingClientRect();
        const mouseX = e.clientX - svgRect.left;
        const mouseY = e.clientY - svgRect.top;
        
        // Calculate zoom center (mouse position or SVG center if mouse is outside)
        const centerX = mouseX >= 0 && mouseX <= svgRect.width ? mouseX : svgRect.width / 2;
        const centerY = mouseY >= 0 && mouseY <= svgRect.height ? mouseY : svgRect.height / 2;
        
        // Calculate new transform to zoom towards the center point
        const scaleChange = newScale / svgTransform.scale;
        const newX = svgTransform.x - (centerX - svgTransform.x) * (scaleChange - 1);
        const newY = svgTransform.y - (centerY - svgTransform.y) * (scaleChange - 1);
        
        setSvgTransform({
          x: newX,
          y: newY,
          scale: newScale
        });
      } else if (!isTrackpadGesture) {
        // Mouse wheel = zoom towards mouse pointer
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.max(0.5, Math.min(6, svgTransform.scale * delta));
        
        // Get mouse position relative to SVG
        const svgRect = e.currentTarget.getBoundingClientRect();
        const mouseX = e.clientX - svgRect.left;
        const mouseY = e.clientY - svgRect.top;
        
        // Calculate zoom center (mouse position or SVG center if mouse is outside)
        const centerX = mouseX >= 0 && mouseX <= svgRect.width ? mouseX : svgRect.width / 2;
        const centerY = mouseY >= 0 && mouseY <= svgRect.height ? mouseY : svgRect.height / 2;
        
        // Calculate new transform to zoom towards the center point
        const scaleChange = newScale / svgTransform.scale;
        const newX = svgTransform.x - (centerX - svgTransform.x) * (scaleChange - 1);
        const newY = svgTransform.y - (centerY - svgTransform.y) * (scaleChange - 1);
        
        setSvgTransform({
          x: newX,
          y: newY,
          scale: newScale
        });
      }
      // For trackpad without Ctrl, let the browser handle scrolling
    };

    // Add event listener with passive: false to allow preventDefault
    svgElement.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      svgElement.removeEventListener('wheel', handleWheel);
    };
  }, [actualSvgRef]);

  // Calculate overall length first (needed by useEffect below)
  const overallLen = parseFloat(lengths['A']) || 0;

  // Auto-update Overall input field when calculated overall length changes
  useEffect(() => {
    if (isEditingOverall) return; // Don't auto-update if user is editing
    // Don't auto-update if the user has manually edited A (unless unit just changed)
    if (lastManualEdit === 'A') {
      return;
    }
    // Only update overallInput if it's not currently being edited
    const currentInputValue = parseFloat(overallInput);
    const calculatedValue = overallLen;
    const displayValue = unit === 'ft' ? (parseFloat(calculatedValue) / 12).toFixed(2).replace(/\.00$/, '') : calculatedValue.toString();
    // Only update if the difference is significant (more than 1 unit) to avoid interfering with typing
    if (isNaN(currentInputValue) || Math.abs(currentInputValue - (unit === 'ft' ? calculatedValue / 12 : calculatedValue)) > 1) {
      setOverallInput(displayValue);
    }
  }, [overallLen, unit, overallInput, lastManualEdit, isEditingOverall]);



  const handleReset = () => {
    // Reset capture tracking
    hasCapturedRef.current = false;
    lastConfigRef.current = '';
    setEditedFields(new Set()); // Clear all edited fields
    setLastManualEdit(null); // Reset manual edit tracking
    setUnit('in');
    setOverallInput(String(defaultLengths.A));
    // Reset capture tracking
    hasCapturedRef.current = false;
    lastConfigRef.current = '';
    // CRITICAL: Always pass lengths in inches to parent component (defaultLengths are already in inches)
    rest.onChange({ connectors: {}, lengths: defaultLengths, specialInstructions: '', audioWireType });
  };

  // Prepare SVG content for Puppeteer rendering using absolute URLs
  const prepareSvgForPuppeteer = useCallback(async (svgContent) => {
    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = svgContent;
      const svgElement = tempDiv.querySelector('svg');

      if (!svgElement) {
        console.warn('⚠️ [SVG-PREP] No SVG element found in content');
        return svgContent;
      }

      const imageElements = svgElement.querySelectorAll('image');
      console.log(`🔄 [SVG-PREP] Found ${imageElements.length} image elements to prepare`);

      // Process each image element
      for (const img of imageElements) {
        const href = img.getAttribute('href');
        if (href) {
          console.log(`🔄 [SVG-PREP] Processing image: ${href}`);
          
          // Convert to absolute URL
          let absoluteUrl;
          if (href.startsWith('data:')) {
            // Keep data URLs as is
            absoluteUrl = href;
            console.log(`🔄 [SVG-PREP] Keeping data URL as is`);
          } else if (href.startsWith('http')) {
            // Already absolute URL
            absoluteUrl = href;
            console.log(`🔄 [SVG-PREP] Already absolute URL: ${absoluteUrl}`);
          } else {
            // Convert relative path to absolute URL
            const baseUrl = 'https://nlfx-cable.luknai.com';
            absoluteUrl = href.startsWith('/') 
              ? `${baseUrl}${href}`
              : `${baseUrl}/${href}`;
            console.log(`🔄 [SVG-PREP] Converted relative path to absolute URL: ${href} → ${absoluteUrl}`);
          }
          
          // Update the href attribute
          img.setAttribute('href', absoluteUrl);
        }
        
        // Remove shadow filter from image elements for Puppeteer capture
        if (img.getAttribute('filter')) {
          console.log(`🔄 [SVG-PREP] Removing filter from image element for Puppeteer capture`);
          img.removeAttribute('filter');
        }
      }
      
      console.log('✅ [SVG-PREP] SVG prepared for Puppeteer rendering with absolute URLs');
      return svgElement.outerHTML;
    } catch (error) {
      console.error('❌ [SVG-PREP] Error preparing SVG for Puppeteer:', error);
      return svgContent;
    }
  }, []);

  // Puppeteer-based image capture function (similar to WyeConnectorSelection)
  const captureWithPuppeteer = useCallback(async (sku) => {
    console.log('🔄 [PUPPETEER-CAPTURE] Starting Puppeteer-based capture...');
    
    try {
      // Generate unique filename
      const timestamp = Date.now();
      const outputFileName = `${sku}_${timestamp}.png`;
      
      // Get the SVG content from the current DOM
      const svgElement = document.getElementById('zip-audio-audio-cable-svg') || actualSvgRef?.current;
      if (!svgElement) {
        console.error('❌ [PUPPETEER-CAPTURE] SVG element not found in DOM');
        return null;
      }
      
      // Prepare SVG content for Puppeteer rendering (use relative paths)
      console.log('🔄 [PUPPETEER-CAPTURE] Preparing SVG content for server-side rendering...');
      const preparedSvg = await prepareSvgForPuppeteer(svgElement.outerHTML);
      
      console.log('📄 [PUPPETEER-CAPTURE] SVG content length:', preparedSvg.length);
      
      // Call the server-side capture endpoint with SVG content
      console.log('📡 [PUPPETEER-CAPTURE] Sending request to:', 'https://nlfx-cable.luknai.com/api/capture-svg');
      
      const response = await fetch('https://nlfx-cable.luknai.com/api/capture-svg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          svgContent: preparedSvg,
          outputFileName: outputFileName
        })
      });
      
      console.log('📡 [PUPPETEER-CAPTURE] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('📡 [PUPPETEER-CAPTURE] Error response body:', errorText);
        throw new Error(`Server responded with ${response.status}: ${response.statusText} - ${errorText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        console.log('✅ [PUPPETEER-CAPTURE] Puppeteer capture successful:', result.s3Url);
        return result.s3Url;
      } else {
        throw new Error(result.error || 'Unknown error from server');
      }
      
    } catch (error) {
      console.error('❌ [PUPPETEER-CAPTURE] Error during Puppeteer capture:', error);
      return null;
    }
  }, [prepareSvgForPuppeteer, actualSvgRef]);

  // Client-side image capture function
  const captureClientSideImage = useCallback(async () => {
    console.log('🔄 [ZIP-AUDIO-AUDIO][CLIENT-CAPTURE] Capturing client-side image...');
    
    if (!actualSvgRef?.current) {
      console.error('❌ [ZIP-AUDIO-AUDIO][CLIENT-CAPTURE] SVG ref not available');
      return null;
    }

    const svgElement = actualSvgRef.current;
    console.log('🔍 [ZIP-AUDIO-AUDIO][CLIENT-CAPTURE] SVG element found:', {
      tagName: svgElement.tagName,
      id: svgElement.id,
      className: svgElement.className,
      width: svgElement.getAttribute('width'),
      height: svgElement.getAttribute('height'),
      viewBox: svgElement.getAttribute('viewBox'),
      offsetWidth: svgElement.offsetWidth,
      offsetHeight: svgElement.offsetHeight
    });

    try {
      // Get SVG dimensions from attributes
      const svgWidth = svgElement.getAttribute('width');
      const svgHeight = svgElement.getAttribute('height');
      const viewBox = svgElement.getAttribute('viewBox');

      // Try capturing the container first (more reliable)
      const container = svgElement.closest('.MuiBox-root') || svgElement.parentElement;
      
      if (container) {
        console.log('🔧 [ZIP-AUDIO-AUDIO][CLIENT-CAPTURE] Capturing container instead of SVG directly');
        
        const canvas = await html2canvas(container, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          foreignObjectRendering: true,
          removeContainer: false,
          width: parseInt(svgWidth) || 800,
          height: parseInt(svgHeight) || 400
        });

        console.log('✅ [ZIP-AUDIO-AUDIO][CLIENT-CAPTURE] Container capture successful');
        
        // Convert to blob
        const blob = await new Promise(resolve => {
          canvas.toBlob(resolve, 'image/png', 0.95);
        });

        console.log('✅ [ZIP-AUDIO-AUDIO][CLIENT-CAPTURE] Client-side image captured successfully');
        return blob;
      } else {
        // Fallback to direct SVG capture
        console.log('🔧 [ZIP-AUDIO-AUDIO][CLIENT-CAPTURE] Capturing SVG directly');
        
        const canvas = await html2canvas(svgElement, {
          width: parseInt(svgWidth) || 800,
          height: parseInt(svgHeight) || 400,
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true,
          foreignObjectRendering: true,
          removeContainer: false,
          onclone: (clonedDoc) => {
            const clonedSvg = clonedDoc.querySelector('svg');
            if (clonedSvg) {
              // Set explicit dimensions for the cloned SVG
              clonedSvg.setAttribute('width', svgWidth || '800');
              clonedSvg.setAttribute('height', svgHeight || '400');
              
              // Preserve the original viewBox if it exists
              if (viewBox) {
                clonedSvg.setAttribute('viewBox', viewBox);
              } else {
                // Set a default viewBox based on width and height
                clonedSvg.setAttribute('viewBox', `0 0 ${svgWidth || 800} ${svgHeight || 400}`);
              }
              
              // Ensure xmlns is set
              clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
              
              console.log('🔧 [ZIP-AUDIO-AUDIO][CLIENT-CAPTURE] Cloned SVG prepared with:', {
                width: clonedSvg.getAttribute('width'),
                height: clonedSvg.getAttribute('height'),
                viewBox: clonedSvg.getAttribute('viewBox')
              });
              
              // Remove any interactive elements that might interfere with capture
              const interactiveElements = clonedSvg.querySelectorAll('[style*="cursor"], [onclick]');
              interactiveElements.forEach(el => {
                el.style.cursor = 'default';
                el.removeAttribute('onclick');
              });
            }
          }
        });

        console.log('🔧 [ZIP-AUDIO-AUDIO][CLIENT-CAPTURE] Cloned document prepared for capture');
        
        // Convert to blob
        const blob = await new Promise(resolve => {
          canvas.toBlob(resolve, 'image/png', 0.95);
        });

        console.log('✅ [ZIP-AUDIO-AUDIO][CLIENT-CAPTURE] Client-side image captured successfully');
        return blob;
      }
    } catch (error) {
      console.error('❌ [ZIP-AUDIO-AUDIO][CLIENT-CAPTURE] Error capturing client-side image:', error);
      return null;
    }
  }, [actualSvgRef]);

  // Register capture function with the service
  useEffect(() => {
    const componentId = 'zip-audio-audio-connector-selection';
    
    const stableCaptureFunction = async () => {
      console.log('🎯 [CLIENT-CAPTURE] Capture function called for:', componentId);
      
      if (!actualSvgRef?.current) {
        console.warn('❌ [CLIENT-CAPTURE] No SVG element found for capture');
        return null;
      }
      
      try {
        console.log('🔄 [CLIENT-CAPTURE] Starting client-side image capture...');
        
        // Add a small delay to ensure SVG is fully rendered
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Use the improved capture function
        return await captureClientSideImage();
        
      } catch (error) {
        console.error('❌ [CLIENT-CAPTURE] Error capturing client-side image:', error);
        return null;
      }
    };
    
    clientImageCaptureService.registerCaptureFunction(componentId, stableCaptureFunction);
    
    return () => {
      console.log('🗑️ [CLIENT-CAPTURE] Unregistering capture function for:', componentId);
      clientImageCaptureService.unregisterCaptureFunction(componentId);
    };
  }, [actualSvgRef, captureClientSideImage]);

  // Add ref to track if we've already captured to prevent repeated captures
  const hasCapturedRef = useRef(false);
  const lastConfigRef = useRef('');

  // Use only lengths and connectors for auto-capture (special instructions don't impact image)
  const currentConfig = JSON.stringify({
    lengths,
    connectors,
    audioWireType,
    unit,
    specialInstructions
  });

  // REMOVED: Auto-capture logic - now triggered only by global Next button
  // The global Next button in CableBuilder will handle image generation
  
  // Keep the readiness check for the Next button
  useEffect(() => {
    // Check if all required lengths are set (A, C, D, E, F)
    const allLengthsSet = ['A', 'C', 'D', 'E', 'F'].every(id => 
      lengths[id] !== undefined && lengths[id] !== null && lengths[id] !== '' && !isNaN(lengths[id])
    );
    
    // Check if all required length fields have been manually edited (show green checkmarks)
    const allRequiredLengthsEdited = ['A', 'C', 'D', 'E', 'F'].every(id => 
      editedFields.has(id)
    );
    
    // Check if all connectors are selected (G, H, I, J)
    const allConnectorsSelected = ['G', 'H', 'I', 'J'].every(id => 
      connectors?.[id]?.id && connectors?.[id]?.gender && connectors?.[id]?.configuration
    );
    
    // All three conditions must be met: lengths set, lengths touched (green checkmarks), and connectors selected
    const isReady = allLengthsSet && allRequiredLengthsEdited && allConnectorsSelected;
    
    if (typeof rest.onReady === 'function') {
      rest.onReady(isReady);
      
      // Track missing requirements for popup
      if (typeof rest.onMissingRequirements === 'function') {
        const missingRequirements = [];
        
        // Check missing lengths
        ['A', 'C', 'D', 'E', 'F'].forEach(id => {
          if (!lengths[id] || lengths[id] === undefined || lengths[id] === null || lengths[id] === '' || isNaN(lengths[id])) {
            missingRequirements.push(`Please set a length for ${id}`);
          } else if (!editedFields.has(id)) {
            missingRequirements.push(`Please interact with length field ${id} (click in the field)`);
          }
        });
        
        // Check missing connectors
        ['G', 'H', 'I', 'J'].forEach(id => {
          if (!connectors?.[id]?.id || !connectors?.[id]?.gender || !connectors?.[id]?.configuration) {
            missingRequirements.push(`Please select a connector for ${id}`);
          }
        });
        
        rest.onMissingRequirements(missingRequirements);
      }
    }
  }, [connectors, lengths, editedFields, audioWireType, rest.onReady, rest.onMissingRequirements]);

  // Initialize editedFields on mount
  useEffect(() => {
    setEditedFields(new Set());
  }, []);

  // Auto-update Overall input field when calculated overall length changes
  useEffect(() => {
    // Don't auto-update if the user has manually edited A
    if (lastManualEdit === 'A') {
      return;
    }
    
    // Only update if the calculated length is significantly different from what's displayed
    const displayValue = unit === 'ft' ? (parseFloat(overallLen) / 12).toFixed(2).replace(/\.00$/, '') : overallLen.toString();
    const currentInput = parseFloat(overallInput);
    const calculatedValue = parseFloat(displayValue);
    
    // Convert currentInput to the same unit as calculatedValue for comparison
    const currentInputInSameUnit = unit === 'ft' ? currentInput * 12 : currentInput;
    
    // Only update if the difference is more than 1 unit (to avoid minor rounding issues)
    if (!isNaN(currentInput) && !isNaN(calculatedValue) && Math.abs(currentInputInSameUnit - overallLen) > 1) {
      setOverallInput(displayValue);
    } else if (isNaN(currentInput) && overallLen > 0) {
      // If current input is invalid, update to calculated value
      setOverallInput(displayValue);
    }
  }, [overallLen, unit, overallInput, lastManualEdit]);

  // Auto-adjust Length B when C, D, E, or F changes
  useEffect(() => {
    const newB = calculateLengthB(lengths);
    if (newB !== lengths['B']) {
      setLengths(prev => ({
        ...prev,
        B: newB
      }));
    }
  }, [lengths['C'], lengths['D'], lengths['E'], lengths['F'], lengths['A'], unit]);

  // Calculate dynamic control points and endpoints for each branch first (like HybridConnectorSelection)
  // Lengths are always stored in inches internally, so use them directly for SVG scaling
  const cLen = PX_PER_INCH * (parseFloat(lengths['C']) || 0);
  const dLen = PX_PER_INCH * (parseFloat(lengths['D']) || 0);
  const eLen = PX_PER_INCH * (parseFloat(lengths['E']) || 0);
  const fLen = PX_PER_INCH * (parseFloat(lengths['F']) || 0);

  // Maximum visual length for SVG display (equivalent to ~50 inches visually)
  const MAX_VISUAL_LENGTH = 300;
  
  // Visual lengths - capped for reasonable SVG display
  const cVisual = Math.min(cLen, MAX_VISUAL_LENGTH);
  const dVisual = Math.min(dLen, MAX_VISUAL_LENGTH);
  const eVisual = Math.min(eLen, MAX_VISUAL_LENGTH);
  const fVisual = Math.min(fLen, MAX_VISUAL_LENGTH);



  // SVG-related variables
  const connW = 50;
  const connH = 50;
  const extraPad = 10;

  // For very short lengths, make the branches visually even shorter
  const minBranch = 3; // visually minimum px for short lengths
  const cShort = Math.max(cVisual, minBranch);
  const dShort = Math.max(dVisual, minBranch);
  const eShort = Math.max(eVisual, minBranch);
  const fShort = Math.max(fVisual, minBranch);

  // Calculate endpoint positions using the same approach as HybridConnectorSelection
  const G_end_adj = { x: 200 - cShort - 50, y: 80 };     // G uses C length (left, top)
  const H_end_adj = { x: 750 + dShort + 50, y: 80 };     // H uses D length (right, top)
  const I_end_adj = { x: 200 - eShort - 50, y: 320 };    // I uses E length (left, bottom)
  const J_end_adj = { x: 750 + fShort + 50, y: 320 };    // J uses F length (right, bottom)

  // Calculate overall line positions using the same logic as HybridConnectorSelection
  const overallLineY = 40;
  const overallLabelY = 25;



  // Calculate dynamic junction points for better centering
  const centerX = (Math.min(G_end_adj.x, I_end_adj.x) + Math.max(H_end_adj.x, J_end_adj.x)) / 2;
  const junctionSpacing = 450; // Fixed spacing between junctions for consistent main cable length
  const leftJunction = { x: centerX - junctionSpacing / 2, y: 80 };
  const rightJunction = { x: centerX + junctionSpacing / 2, y: 80 };





  // For zoom-out, include the outermost edges of connector boxes
  const allX = [
    G_end_adj.x - extraPad, // left edge of G
    I_end_adj.x - extraPad, // left edge of I
    H_end_adj.x + connW + extraPad, // right edge of H
    J_end_adj.x + connW + extraPad, // right edge of J
    leftJunction.x, rightJunction.x  // Dynamic junction points
  ];
  
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);

  // Auto-scaling logic - simplified for better centering
  const pad = 40;
  const contentWidth = maxX - minX;
  
  // Calculate actual content bounds including labels - ensure A label isn't cut off
  const minY = Math.min(G_end_adj.y, H_end_adj.y, overallLabelY) - 40 - 15; // Include overall label
  const maxY = Math.max(I_end_adj.y, J_end_adj.y) + 40 + 15; // Bottom connectors + label
  const contentHeight = maxY - minY;
  
  // Calculate viewBox for proper centering with adequate padding for labels
  const viewBoxWidth = contentWidth + 2 * pad;
  const viewBoxHeight = contentHeight + 2 * pad; // More padding for labels
  const viewBoxX = minX - pad - 20; // Slight shift left to center better in viewport
  const viewBoxY = minY - pad; // Full padding at top for A label
  
  // No transform needed - let SVG handle centering
  const groupStyle = {
    transition: 'all 0.3s ease',
  };

  // Dynamic label positions - positioned along each wire path using visual lengths
  const cMid = { x: leftJunction.x - (cVisual * 0.6), y: leftJunction.y - (cVisual * 0.3) }; // Along C wire path (G)
  const dMid = { x: rightJunction.x + (dVisual * 0.6), y: rightJunction.y - (dVisual * 0.3) }; // Along D wire path (H)
  const eMid = { x: leftJunction.x - (eVisual * 0.6), y: leftJunction.y + 100 + (eVisual * 0.3) }; // Along E wire path (I)
  const fMid = { x: rightJunction.x + (fVisual * 0.6), y: rightJunction.y + 100 + (fVisual * 0.3) }; // Along F wire path (J)

  // Calculate the true leftmost and rightmost endpoints for overall length
  const leftmostX = Math.min(G_end_adj.x, I_end_adj.x);
  const rightmostX = Math.max(H_end_adj.x, J_end_adj.x);

  // Input handlers (same as Hybrid, but no power logic)
  const handlePointClick = (id) => {
    setSelectedPoint(id);
    setModalOpen(true);
  };

  return (
    <Box sx={{ width: '95%', maxWidth: 1200, margin: '0 auto', p: 1 }}>
      {/* Header with Reset button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" component="h2">
          Configure Zip Audio+Audio Connectors
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            onClick={handleReset} 
            variant="outlined" 
            sx={{ 
              fontSize: '0.8rem',
              px: 2,
              py: 0.5
            }}
          >
            RESET
          </Button>

        </Box>
      </Box>
      
      {/* Unit toggle and Audio Wire Type selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1">Units:</Typography>
          <ToggleButtonGroup
            value={unit}
            exclusive
            onChange={handleUnitChange}
            aria-label="unit selection"
            size="small"
          >
            <ToggleButton value="in" aria-label="inches">Inches</ToggleButton>
            <ToggleButton value="ft" aria-label="feet">Feet</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ minWidth: 200 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Cable Type:
          </Typography>
          <TextField
            value="PSZ - Zip Cable"
            size="small"
            fullWidth
            variant="outlined"
            disabled={true}
            InputProps={{
              readOnly: true,
              style: { backgroundColor: '#f5f5f5' }
            }}
            helperText="Zip cables always use PSZ wire type"
          />
        </Box>
      </Box>

      {/* Length fields ABOVE SVG */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {lengthLabels.map(({ id, label }) => {
          const edited = isEdited(id);
          const displayVal = unit === 'ft' ? (parseFloat(lengths[id] || 0) / 12).toFixed(2).replace(/\.00$/, '') : lengths[id] || '';
          
          if (id === 'A') {
            return (
              <Grid item xs={6} md={2} key={id}>
                <TextField
                  label="A (Overall)"
                  value={overallInput}
                  onFocus={() => {
                    setEditedFields(prev => new Set([...prev, 'A']));
                    setLastManualEdit('A');
                  }}
                  onChange={e => handleOverallInputChange(e.target.value)}
                  onBlur={commitOverallInput}
                  onKeyDown={e => { if (e.key === 'Enter') commitOverallInput(); }}
                  type="number"
                  inputProps={{ min: 0, step: unit === 'ft' ? 1/12 : 1 }}
                  fullWidth
                  InputProps={{
                    endAdornment: <span style={{ display: 'flex', alignItems: 'center' }}>{unit === 'ft' ? <span style={{ marginLeft: 4 }}>'</span> : <span style={{ marginLeft: 4 }}>&quot;</span>}{edited && <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18, ml: 0.5 }} titleAccess="User set" />}</span>
                  }}
                  helperText={unit === 'ft' ? "Max: 500 feet" : "Max: 6000 inches"}
                />
              </Grid>
            );
          }
          if (id === 'B') {
            return (
              <Grid item xs={6} md={2} key={id}>
                <TextField
                  label={label}
                  value={displayVal}
                  disabled
                  fullWidth
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                      backgroundColor: '#f5f5f5',
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        {unit === 'ft' ? (
                          <span style={{ marginLeft: 4 }}>'</span>
                        ) : (
                          <span style={{ marginLeft: 4 }}>&quot;</span>
                        )}
                        <Tooltip title="Calculated automatically"><WarningIcon color="action" sx={{ fontSize: 18, ml: 0.5 }} /></Tooltip>
                      </span>
                    )
                  }}
                />
              </Grid>
            );
          }
          return (
            <Grid item xs={6} md={2} key={id}>
              <TextField
                label={label}
                value={tempInputs[id] !== undefined ? tempInputs[id] : displayVal}
                onChange={(e) => handleLengthChange(id, e.target.value)}
                onFocus={() => handleLengthFocus(id)}
                onBlur={() => handleLengthBlur(id)}
                onKeyDown={(e) => { if (e.key === 'Enter') commitLengthInput(id); }}
                type="number"
                inputProps={{ 
                  min: 0, 
                  step: unit === 'ft' ? 1/12 : 1 
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {unit === 'ft' ? (
                        <span style={{ marginLeft: 4 }}>'</span>
                      ) : (
                        <span style={{ marginLeft: 4 }}>&quot;</span>
                      )}
                      {edited && <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18, ml: 0.5 }} titleAccess="User set" />}
                    </span>
                  )
                }}
                helperText={['C', 'D', 'E', 'F'].includes(id) ? (unit === 'ft' ? "Max: 10 feet" : "Max: 120 inches") : (unit === 'ft' ? "Max: 500 feet" : "Max: 6000 inches")}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* SVG with proper viewBox and preserveAspectRatio */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mb: 2,
        width: '100%',
        border: '1px solid #ddd',
        borderRadius: 2,
        cursor: isPanning ? 'grabbing' : 'grab',
        '& svg': {
          height: 'auto'
        }
      }}>
        <svg 
          ref={actualSvgRef}
          id="zip-audio-audio-cable-svg"
          width="800"
          height="400"
          viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`} 
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          style={{ 
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
          onMouseDown={handleSvgMouseDown}
          onMouseMove={handleSvgMouseMove}
          onMouseUp={handleSvgMouseUp}
          onMouseLeave={handleSvgMouseUp}
          onTouchStart={handleSvgTouchStart}
          onTouchMove={handleSvgTouchMove}
          onTouchEnd={handleSvgTouchEnd}
        >
          {/* Shadow filter */}
          <defs>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
            </filter>
          </defs>
          
          <g 
            style={groupStyle}
            transform={`translate(${svgTransform.x}, ${svgTransform.y}) scale(${svgTransform.scale})`}
          >
            {/* Central wire (B) - Represents the main trunk cable length - connects the junction points */}
            {/* <path 
              d={`M ${leftJunction.x} 200 L ${rightJunction.x} 200`}
              stroke="#333" 
              strokeWidth="12" 
              fill="none" 
              filter="url(#shadow)"
              strokeLinecap="round"
            /> */}
            
            {/* B length trunk line - Combined main cable */}
            <path 
              d={`M ${leftJunction.x} 206 L ${rightJunction.x} 206`}
              stroke="#555" 
              strokeWidth="12" 
              fill="none" 
              strokeLinecap="round"
            />
            
            {/* Cable sheath outline for better visibility */}
            <rect x={leftJunction.x} y="200" width={rightJunction.x - leftJunction.x} height="12" rx="6" fill="#000" stroke="#b32b2b" strokeWidth=".5" opacity="0.7" />
            
            
            {/* Wire branches - Color-coded for audio pairs with prettier curves */}
            {/* G and H are paired - both use audio1 - Lighter blue color */}
            <path d={`M ${leftJunction.x} 201 C ${leftJunction.x - 30} 180 ${leftJunction.x - cVisual * 0.7} 140 ${leftJunction.x - cVisual - 10} 110 C ${leftJunction.x - cVisual - 25} 95 ${G_end_adj.x + 15} ${G_end_adj.y + 15} ${G_end_adj.x} ${G_end_adj.y}`} stroke="#6495c8" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
            <path d={`M ${rightJunction.x} 201 C ${rightJunction.x + 30} 180 ${rightJunction.x + dVisual * 0.7} 140 ${rightJunction.x + dVisual + 10} 110 C ${rightJunction.x + dVisual + 25} 95 ${H_end_adj.x - 15} ${H_end_adj.y + 15} ${H_end_adj.x} ${H_end_adj.y}`} stroke="#6495c8" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
            
            {/* I and J are paired - both use audio2 - Light green color */}
            <path d={`M ${leftJunction.x} 211 C ${leftJunction.x - 30} 230 ${leftJunction.x - eVisual * 0.7} 270 ${leftJunction.x - eVisual - 10} 300 C ${leftJunction.x - eVisual - 25} 315 ${I_end_adj.x + 15} ${I_end_adj.y - 15} ${I_end_adj.x} ${I_end_adj.y}`} stroke="#4caf50" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
            <path d={`M ${rightJunction.x} 211 C ${rightJunction.x + 30} 230 ${rightJunction.x + fVisual * 0.7} 270 ${rightJunction.x + fVisual + 10} 300 C ${rightJunction.x + fVisual + 25} 315 ${J_end_adj.x - 15} ${J_end_adj.y - 15} ${J_end_adj.x} ${J_end_adj.y}`} stroke="#4caf50" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
            
            {/* Connector boxes - G (boxes disappear when connector is selected) */}
            <g onClick={() => handlePointClick('G')} style={{ cursor: 'pointer' }}>
                          {connectors?.['G']?.image ? (
              <image 
                href={getImageUrlForDisplay(connectorImages['G'] || connectors?.['G']?.image)}
                  x={G_end_adj.x - connW / 2} 
                  y={G_end_adj.y - connH / 2} 
                  width={connW} 
                  height={connH} 
                  filter="url(#shadow)"
                />
              ) : (
                <rect x={G_end_adj.x - connW / 2} y={G_end_adj.y - connH / 2} width={connW} height={connH} rx="6" fill="#6495c8" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />
              )}
              <text x={G_end_adj.x} y={G_end_adj.y - 30} fontSize="14" fill="#000" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">G</text>
            </g>
            
            {/* Connector boxes - H (boxes disappear when connector is selected) */}
            <g onClick={() => handlePointClick('H')} style={{ cursor: 'pointer' }}>
                          {connectors?.['H']?.image ? (
              <image 
                href={getImageUrlForDisplay(connectorImages['H'] || connectors?.['H']?.image)}
                  x={H_end_adj.x - connW / 2} 
                  y={H_end_adj.y - connH / 2} 
                  width={connW} 
                  height={connH} 
                  filter="url(#shadow)"
                />
              ) : (
                <rect x={H_end_adj.x - connW / 2} y={H_end_adj.y - connH / 2} width={connW} height={connH} rx="6" fill="#6495c8" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />
              )}
              <text x={H_end_adj.x} y={H_end_adj.y - 30} fontSize="14" fill="#000" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">H</text>
            </g>
            
            {/* Connector boxes - I (boxes disappear when connector is selected) */}
            <g onClick={() => handlePointClick('I')} style={{ cursor: 'pointer' }}>
                          {connectors?.['I']?.image ? (
              <image 
                href={getImageUrlForDisplay(connectorImages['I'] || connectors?.['I']?.image)}
                  x={I_end_adj.x - connW / 2} 
                  y={I_end_adj.y - connH / 2} 
                  width={connW} 
                  height={connH} 
                  filter="url(#shadow)"
                />
              ) : (
                <rect x={I_end_adj.x - connW / 2} y={I_end_adj.y - connH / 2} width={connW} height={connH} rx="6" fill="#4caf50" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />
              )}
              <text x={I_end_adj.x} y={I_end_adj.y + 40} fontSize="14" fill="#000" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">I</text>
            </g>
            
            {/* Connector boxes - J (boxes disappear when connector is selected) */}
            <g onClick={() => handlePointClick('J')} style={{ cursor: 'pointer' }}>
                          {connectors?.['J']?.image ? (
              <image 
                href={getImageUrlForDisplay(connectorImages['J'] || connectors?.['J']?.image)}
                  x={J_end_adj.x - connW / 2} 
                  y={J_end_adj.y - connH / 2} 
                  width={connW} 
                  height={connH} 
                  filter="url(#shadow)"
                />
              ) : (
                <rect x={J_end_adj.x - connW / 2} y={J_end_adj.y - connH / 2} width={connW} height={connH} rx="6" fill="#4caf50" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />
              )}
              <text x={J_end_adj.x} y={J_end_adj.y + 40} fontSize="14" fill="#000" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">J</text>
            </g>
            
            {/* Overall dimension line - Professional style */}
            <line x1={leftmostX} y1={overallLineY} x2={rightmostX} y2={overallLineY} stroke="#bbb" strokeWidth="2" />
            <line x1={leftmostX} y1={overallLineY-5} x2={leftmostX} y2={overallLineY+5} stroke="#bbb" strokeWidth="2" />
            <line x1={rightmostX} y1={overallLineY-5} x2={rightmostX} y2={overallLineY+5} stroke="#bbb" strokeWidth="2" />
            <rect x={(leftmostX + rightmostX) / 2 - 48} y={overallLabelY - 20} width="96" height="24" rx="8" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={(leftmostX + rightmostX) / 2} y={overallLabelY - 8} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500" dominantBaseline="middle">
              {unit === 'ft' ? `A = ${(parseFloat(overallLen) / 12).toFixed(2).replace(/\.00$/, '')}'` : `A = ${overallLen}"`}
            </text>
            
            {/* Length labels with oval outlined boxes - no dimension lines */}
            {/* B label positioned on the middle wire - centered between junctions */}
            <rect x={(leftJunction.x + rightJunction.x) / 2 - 48} y={206 - 10} width="96" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={(leftJunction.x + rightJunction.x) / 2} y={206 + 5} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {unit === 'ft' ? `B = ${(parseFloat(lengths['B']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `B = ${parseFloat(lengths['B']) || 0}"`}
            </text>
            
            <rect x={cMid.x-32} y={cMid.y+40} width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={cMid.x} y={cMid.y+55} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {lengths['C'] ? (unit === 'ft' ? `C = ${(parseFloat(lengths['C']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `C = ${lengths['C']}"`) : 'C'}
            </text>
            
            <rect x={dMid.x-32} y={dMid.y+40} width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={dMid.x} y={dMid.y+55} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {lengths['D'] ? (unit === 'ft' ? `D = ${(parseFloat(lengths['D']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `D = ${lengths['D']}"`) : 'D'}
            </text>
            
            <rect x={eMid.x-32} y={eMid.y+80} width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={eMid.x} y={eMid.y+95} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {lengths['E'] ? (unit === 'ft' ? `E = ${(parseFloat(lengths['E']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `E = ${lengths['E']}"`) : 'E'}
            </text>
            
            <rect x={fMid.x-32} y={fMid.y+80} width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
            <text x={fMid.x} y={fMid.y+95} textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
              {lengths['F'] ? (unit === 'ft' ? `F = ${(parseFloat(lengths['F']) / 12).toFixed(2).replace(/\.00$/, '')}'` : `F = ${lengths['F']}"`) : 'F'}
            </text>
          </g>
        </svg>
      </Box>

      {/* Connector summary row BELOW SVG with actual connector images and configuration options */}
      <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        {connectorPoints.map(({ id, label }) => {
          const connector = connectors?.[id];
          const isSelected = connector?.type;
          const currentConfig = connector?.configuration || 'standard';
          let connectorLabel = 'Not selected';
          if (connector?.id) {
            // Get the proper description from the compatibility matrix
            const matrixEntry = compatibilityMatrix[connector.id];
            if (matrixEntry?.description) {
              connectorLabel = matrixEntry.description;
            } else if (connector.description) {
              connectorLabel = connector.description;
            } else if (connector.label) {
              connectorLabel = connector.label;
            } else {
              connectorLabel = connector.id;
            }
          }
          
          return (
            <Grid item xs={6} md={3} key={id}>
              <Paper variant="outlined" sx={{ p: 1, cursor: 'pointer', minHeight: 120, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }} onClick={() => handlePointClick(id)}>
                {/* Clear button - positioned absolutely in top-right corner */}
                {connector?.id && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearConnector(id);
                    }}
                    sx={{ 
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      color: '#d32f2f',
                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(211, 47, 47, 0.2)',
                        color: '#b71c1c'
                      },
                      width: 24,
                      height: 24,
                      zIndex: 1
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                    {/* Color-coded box for cable type */}
                    <Box sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: (id === 'G' || id === 'H') ? '#6495c8' : '#4caf50',
                      border: '1px solid #fff',
                      borderRadius: '2px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }} />
                    <span role="img" aria-label="audio" style={{ fontSize: '16px' }}>🎵</span>
                    {connector?.image ? (
                      <img src={connectorImages[id] || connector.image} alt={connector.label} style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
                    ) : (
                      <Box sx={{ width: 40, height: 40, border: '2px dashed #ccc', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#999' }}>?</Box>
                    )}
                    {connector?.type && <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18, ml: 0.5 }} titleAccess="Selected" />}
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary" align="center">Connector {label}</Typography>
                  <Typography variant="body2" align="center" sx={{ mt: 0.5 }}>
                    {connectorLabel}
                    {isXLR5Pin(connector) && (
                      <Tooltip title="Only 3 pins wired" arrow>
                        <span style={{ color: '#ff9800', marginLeft: '4px', fontWeight: 'bold' }}>*</span>
                      </Tooltip>
                    )}
                  </Typography>
                </Box>
                
                {/* Configuration Selector - show when connector is selected */}
                {isSelected && (
                  <Box sx={{ mt: 'auto', pt: 1, borderTop: '1px solid', borderColor: 'divider', width: '100%' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem', textAlign: 'center' }}>
                      Configuration:
                    </Typography>
                    <ToggleButtonGroup
                      value={currentConfig}
                      exclusive
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, newConfig) => {
                        e.stopPropagation();
                        if (newConfig !== null) {
                          handleConfigurationChange(id, newConfig, e);
                        }
                      }}
                      aria-label={`configuration for connector ${label}`}
                      size="small"
                      sx={{ 
                        width: '100%',
                        '& .MuiToggleButton-root': {
                          fontSize: '0.65rem',
                          px: 0.25,
                          py: 0.2,
                          minWidth: 'unset',
                          flex: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          '&.Mui-selected': {
                            backgroundColor: '#e8f5e8',
                            color: '#2e7d32',
                            border: '1px solid #4caf50',
                            '&:hover': {
                              backgroundColor: '#dcedc8'
                            }
                          }
                        }
                      }}
                    >
                      {(() => {
                        const standardCompat = checkConfigurationCompatibility(id, 'standard');
                        const rightAngleCompat = checkConfigurationCompatibility(id, 'right-angle');
                        const panelMountCompat = checkConfigurationCompatibility(id, 'panel-mount');
                        
                        return (
                          <>
                            <Tooltip 
                              title={
                                !getConfigurationsForConnector(connector).standard
                                  ? "Configuration not available for this connector type"
                                  : standardCompat?.incompatible 
                                    ? `Incompatible: ${standardCompat.reason}` 
                                    : "Standard configuration"
                              }
                              arrow
                            >
                              <ToggleButton 
                                value="standard" 
                                aria-label="standard"
                                disabled={!getConfigurationsForConnector(connector).standard}
                                sx={{ 
                                  flex: 1,
                                  minWidth: 0,
                                  px: 0.5,
                                  ...(standardCompat?.incompatible && {
                                    backgroundColor: '#ffebee',
                                    color: '#c62828',
                                    '&:hover': {
                                      backgroundColor: '#ffcdd2'
                                    }
                                  }),
                                  '&.Mui-selected': {
                                    backgroundColor: '#e8f5e8',
                                    color: '#2e7d32',
                                    border: '1px solid #4caf50',
                                    '&:hover': {
                                      backgroundColor: '#dcedc8'
                                    }
                                  }
                                }}
                              >
                                STD
                              </ToggleButton>
                            </Tooltip>
                            <Tooltip 
                              title={
                                !getConfigurationsForConnector(connector)['right-angle']
                                  ? "Configuration not available for this connector type"
                                  : rightAngleCompat?.incompatible 
                                    ? `Incompatible: ${rightAngleCompat.reason}` 
                                    : "Right Angle (90°) connector"
                              }
                              arrow
                            >
                              <ToggleButton 
                                value="right-angle" 
                                aria-label="right angle"
                                disabled={!getConfigurationsForConnector(connector)['right-angle']}
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  px: 0.5,
                                  ...(rightAngleCompat?.incompatible && {
                                    backgroundColor: '#ffebee',
                                    color: '#c62828',
                                    '&:hover': {
                                      backgroundColor: '#ffcdd2'
                                    }
                                  }),
                                  '&.Mui-disabled': { 
                                    color: 'rgba(0, 0, 0, 0.26)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.12)'
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: '#e8f5e8',
                                    color: '#2e7d32',
                                    border: '1px solid #4caf50',
                                    '&:hover': {
                                      backgroundColor: '#dcedc8'
                                    }
                                  }
                                }}
                              >
                                RA
                              </ToggleButton>
                            </Tooltip>
                            <Tooltip 
                              title={
                                !getConfigurationsForConnector(connector)['panel-mount']
                                  ? "Configuration not available for this connector type"
                                  : panelMountCompat?.incompatible 
                                    ? `Incompatible: ${panelMountCompat.reason}` 
                                    : "Panel-mount configuration"
                              }
                              arrow
                            >
                              <ToggleButton 
                                value="panel-mount" 
                                aria-label="panel mount"
                                disabled={!getConfigurationsForConnector(connector)['panel-mount']}
                                sx={{ 
                                  flex: 1,
                                  minWidth: 0,
                                  px: 0.5,
                                  ...(panelMountCompat?.incompatible && {
                                    backgroundColor: '#ffebee',
                                    color: '#c62828',
                                    '&:hover': {
                                      backgroundColor: '#ffcdd2'
                                    }
                                  }),
                                  '&.Mui-selected': {
                                    backgroundColor: '#e8f5e8',
                                    color: '#2e7d32',
                                    border: '1px solid #4caf50',
                                    '&:hover': {
                                      backgroundColor: '#dcedc8'
                                    }
                                  },
                                  '&.Mui-disabled': { 
                                    color: 'rgba(0, 0, 0, 0.26)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.12)'
                                  }
                                }}
                              >
                                PNL
                              </ToggleButton>
                            </Tooltip>
                          </>
                        );
                      })()}
                    </ToggleButtonGroup>
                  </Box>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Special Instructions */}
      <TextField
        label="Special Instructions (Optional)"
        multiline
        rows={3}
        fullWidth
        value={specialInstructions}
        onChange={handleInstructionsChange}
        placeholder="How are you going to use this cable? We evaluate every cable we build... Please DO NOT include any personal information!"
      />

      {/* Compatibility Warnings */}
      {Object.keys(compatibilityWarnings).length > 0 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          {Object.entries(compatibilityWarnings).map(([key, warning]) => (
            <Alert 
              key={key}
              severity={warning.type || 'warning'} 
              sx={{ mb: 1 }}
              icon={<WarningIcon />}
            >
              <Typography variant="body2" fontWeight="bold">
                Connector Compatibility Note: {key.replace('-', ' ↔ ')}
              </Typography>
              <Typography variant="body2">
                {warning.message}
              </Typography>
              {warning.details && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {warning.details}
                </Typography>
              )}
            </Alert>
          ))}
        </Box>
      )}

      {/* Modal for connector selection */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="connector-modal-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="connector-modal-title">
          Select Audio Connector for Point {selectedPoint}
        </DialogTitle>
        <DialogContent>
          <ConnectorModal 
            selectedPoint={selectedPoint} 
            connectors={connectors} 
            onConnectorSelect={handleConnectorSelect} 
            type={rest.type}
            findConnectorVariant={findConnectorVariant}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      
      {/* Tooltip for configuration change notifications */}
      <Tooltip
        open={tooltipOpen}
        anchorEl={tooltipAnchor}
        onClose={() => setTooltip(false)}
        title={tooltipMessage}
        arrow
        placement="top"
        sx={{
          '& .MuiTooltip-tooltip': {
            backgroundColor: '#d32f2f',
            color: 'white',
            fontSize: '0.875rem',
            maxWidth: 300
          }
        }}
      />
    </Box>
  );
}

export default ZipAudioAudioConnectorSelection;